
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

export default function Cases() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const cases = [
    {
      image: 'https://public.readdy.ai/ai/img_res/ff0110aa0cf940a8938f7e8e7bb6f6a4.jpg',
      title: t('cases.case1'),
      description: t('cases.case1Desc'),
      category: 'Residential'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20commercial%20building%20with%20soft%20reflective%20glass%20facade%2C%20peaceful%20office%20tower%20with%20gentle%20natural%20light%20and%20warm%20ambient%20glow%2C%20serene%20contemporary%20business%20architecture%20with%20delicate%20sky%20reflections%2C%20tranquil%20urban%20commercial%20space%20with%20inviting%20glass%20curtain%20wall%20and%20soft%20pastel%20tones%2C%20comfortable%20professional%20environment%20with%20natural%20daylight%20filtering%20through%20clean%20glass%20panels&width=800&height=600&seq=case-soft-502&orientation=landscape',
      title: t('cases.case2'),
      description: t('cases.case2Desc'),
      category: 'Commercial'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Luxurious%20modern%20villa%20with%20expansive%20sliding%20doors%20at%20sunset%2C%20warm%20golden%20light%20streaming%20through%20large%20glass%20panels%20into%20cozy%20living%20space%2C%20peaceful%20high%20end%20home%20with%20seamless%20indoor%20outdoor%20connection%20and%20soft%20ambient%20glow%2C%20serene%20contemporary%20luxury%20residence%20with%20comfortable%20furnishings%20and%20gentle%20evening%20atmosphere%2C%20tranquil%20premium%20property%20with%20inviting%20warm%20lighting%20and%20natural%20materials&width=800&height=600&seq=case-soft-503&orientation=landscape',
      title: t('cases.case3'),
      description: t('cases.case3Desc'),
      category: 'Residential'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Serene%20spa-like%20bathroom%20with%20frameless%20glass%20shower%20and%20soft%20natural%20light%2C%20peaceful%20contemporary%20bathroom%20renovation%20with%20warm%20ambient%20glow%20and%20delicate%20shadows%2C%20tranquil%20luxury%20bathing%20space%20with%20gentle%20lighting%20on%20natural%20stone%20and%20warm%20neutral%20tones%2C%20comfortable%20modern%20bathroom%20with%20inviting%20atmosphere%20and%20soft%20textures%2C%20calming%20spa%20environment%20with%20natural%20materials%20and%20diffused%20daylight&width=800&height=600&seq=case-soft-504&orientation=landscape',
      title: t('cases.case4'),
      description: t('cases.case4Desc'),
      category: 'Residential'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Bright%20inviting%20office%20space%20with%20elegant%20glass%20partition%20walls%2C%20soft%20natural%20daylight%20filtering%20through%20transparent%20dividers%20into%20comfortable%20workspace%2C%20peaceful%20modern%20corporate%20environment%20with%20warm%20ambient%20lighting%20and%20gentle%20shadows%2C%20serene%20contemporary%20office%20design%20with%20clean%20glass%20panels%20and%20inviting%20atmosphere%2C%20tranquil%20professional%20space%20with%20natural%20light%20and%20calming%20neutral%20color%20palette&width=800&height=600&seq=case-soft-505&orientation=landscape',
      title: t('cases.case5'),
      description: t('cases.case5Desc'),
      category: 'Commercial'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20glass%20sunroom%20addition%20with%20soft%20natural%20light%20and%20peaceful%20garden%20view%2C%20warm%20inviting%20conservatory%20with%20floor%20to%20ceiling%20windows%20and%20gentle%20ambient%20glow%2C%20serene%20modern%20glass%20room%20extension%20with%20comfortable%20furnishings%20and%20delicate%20shadows%2C%20tranquil%20residential%20space%20with%20lush%20greenery%20visible%20outside%20and%20cozy%20interior%20atmosphere%2C%20peaceful%20home%20addition%20with%20natural%20materials%20and%20warm%20welcoming%20environment&width=800&height=600&seq=case-soft-506&orientation=landscape',
      title: t('cases.case6'),
      description: t('cases.case6Desc'),
      category: 'Residential'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-amber-50/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('cases.badge')}</p>
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
            {t('cases.title')}
          </h2>
          <div className="w-12 h-px bg-amber-400 mx-auto mb-6"></div>
          <p className="text-stone-500 text-sm max-w-2xl mx-auto leading-relaxed font-light">
            {t('cases.subtitle')}
          </p>
        </div>

        {/* Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cases.map((caseItem, index) => (
            <div 
              key={index}
              className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ height: '320px', transitionDelay: `${index * 100}ms` }}
            >
              <img 
                src={caseItem.image}
                alt={caseItem.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-800/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>
              
              {/* Category Tag */}
              <div className="absolute top-4 left-4">
                <span className="bg-white/15 backdrop-blur-md text-white text-[10px] font-medium px-3 py-1.5 rounded-full tracking-wider uppercase border border-white/20">
                  {caseItem.category}
                </span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-lg font-medium text-white mb-1.5">{caseItem.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{caseItem.description}</p>
                <div className="mt-4 flex items-center gap-2 text-amber-300 text-xs font-medium opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <span>{t('products.viewDetails')}</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
